/**
 * Uscita audio delle voci (anti-eco).
 *
 * Perché un elemento <audio> e non `ctx.destination`:
 * su Chromium la cancellazione dell'eco (AEC) di getUserMedia usa come riferimento l'audio che il
 * browser riproduce tramite elementi media/WebRTC, ma NON quello che esce da
 * `AudioContext.destination`. Se le voci escono da `ctx.destination` chi usa le casse rimanda agli
 * altri la loro voce (eco) e, con due persone con le casse, si innesca il Larsen.
 * Quindi il mix finale (bus del ducking, dopo i gain dei peer) va in un
 * MediaStreamAudioDestinationNode e il suo stream è riprodotto da UN solo elemento <audio>
 * nascosto, `autoplay`, non muted. Il dispositivo di uscita si sceglie con `audioEl.setSinkId()`
 * (lo stesso dispositivo su cui l'AEC prende il riferimento).
 *
 * Firefox e Safari supportano MediaStreamAudioDestinationNode + <audio>, quindi il percorso è lo
 * stesso ovunque. Se manca (browser molto vecchio o ambiente senza DOM, es. i test) si torna a
 * `ctx.destination`, cioè al comportamento precedente.
 */

/** Il sottoinsieme di HTMLAudioElement che usiamo (mockabile nei test). */
export interface OutputElementLike {
  srcObject: MediaProvider | null;
  autoplay: boolean;
  muted: boolean;
  play(): Promise<void>;
  pause(): void;
  setSinkId?: (id: string) => Promise<void>;
  remove?: () => void;
}

export function createHiddenAudioElement(): OutputElementLike | null {
  if (typeof document === 'undefined' || !document.body) return null;
  const el = document.createElement('audio');
  el.autoplay = true;
  el.muted = false;
  el.setAttribute('playsinline', '');
  el.setAttribute('aria-hidden', 'true');
  el.dataset.testid = 'voice-output';
  el.style.display = 'none';
  document.body.appendChild(el);
  return el;
}

export class AudioOutput {
  private readonly dest: MediaStreamAudioDestinationNode | null = null;
  private readonly el: OutputElementLike | null = null;
  private blocked = false;

  constructor(
    private readonly ctx: AudioContext,
    private readonly input: AudioNode,
    createElement: () => OutputElementLike | null,
  ) {
    const canStream = typeof (ctx as Partial<AudioContext>).createMediaStreamDestination === 'function';
    const el = canStream ? createElement() : null;
    if (el) {
      this.dest = ctx.createMediaStreamDestination();
      this.dest.channelCount = 2;
      input.connect(this.dest);
      el.autoplay = true;
      el.muted = false;
      el.srcObject = this.dest.stream;
      this.el = el;
    } else {
      // Ripiego: comportamento precedente (niente riferimento AEC su Chromium).
      input.connect(ctx.destination);
    }
  }

  /** True se le voci escono dall'elemento <audio> (percorso anti-eco). */
  get usesElement(): boolean {
    return this.el !== null;
  }

  /** L'elemento non è partito per la politica di autoplay. */
  get isBlocked(): boolean {
    return this.blocked;
  }

  get element(): OutputElementLike | null {
    return this.el;
  }

  /**
   * Avvia la riproduzione. Va chiamata nel gesto dell'utente (clic su "Entra" / "Attiva audio"),
   * prima di qualunque await: Safari concede l'autoplay solo dentro il gesto.
   * Restituisce true se l'audio è in riproduzione.
   */
  async play(): Promise<boolean> {
    if (!this.el) return true;
    try {
      await this.el.play();
      this.blocked = false;
    } catch {
      this.blocked = true;
    }
    return !this.blocked;
  }

  /** True se si può scegliere il dispositivo d'uscita su questo browser. */
  get canSetSink(): boolean {
    return !!this.el && typeof this.el.setSinkId === 'function';
  }

  /** Cambia il dispositivo d'uscita ('' = predefinito). False se non supportato o fallito. */
  async setSinkId(deviceId: string): Promise<boolean> {
    if (!this.el || typeof this.el.setSinkId !== 'function') return false;
    try {
      await this.el.setSinkId(deviceId);
      return true;
    } catch {
      return false;
    }
  }

  dispose(): void {
    try {
      this.input.disconnect(this.dest ?? this.ctx.destination);
    } catch {
      /* già scollegato */
    }
    if (this.el) {
      this.el.pause();
      this.el.srcObject = null;
      this.el.remove?.();
    }
  }
}
